import { MASTER_COLOR_LIST, getColorChoices, getButtonChoices, getDefaultButton } from '../../../config/colors';

const blockBgColorList = [
  ['none', 'None (transparent)', 'light'],
  ...MASTER_COLOR_LIST,
];

const schema = (formData = {}) => {
  const cardBg = formData.cardBackgroundColor;
  const blockBg = formData.blockBackgroundColor;
  const showStats = !!formData.useStatistics;

  const fieldsets = [
    {
      id: 'default',
      title: 'Default',
      fields: [
        'customClass',
        'preheaderText',
        'headerText',
        'descriptionText',
        'headerAlignment',
        'blockAlignment',
        'columns',
        'imagePosition',
        'blockBackgroundColor',
        'showViewAll',
        'viewAllText',
        'viewAllUrl',
        'viewAllStyle',
        'useStatistics',
      ],
    },
    {
      id: 'mobile',
      title: 'Mobile carousel',
      fields: [
        'mobileCarousel',
        'mobileCarouselAutoplay',
        'mobileCarouselDots',
        'carouselControlsColor',
      ],
    },
  ];

  const showStyling =
    formData.imagePosition && formData.imagePosition !== 'background';
  if (showStyling && !showStats) {
    fieldsets.push({
      id: 'styling',
      title: 'Card Styling',
      fields: ['cardBackgroundColor'],
    });
  }

  if (showStats) {
    fieldsets.push({
      id: 'statistics',
      title: 'Statistics',
      fields: ['statsFormatK', 'statAnimationMs', 'statistics'],
    });
  } else {
    fieldsets.push({
      id: 'cards',
      title: 'Cards',
      fields: ['items'],
    });
  }

  return {
    title: 'Multi Card Block',
    fieldsets,
    properties: {
      customClass: {
        title: 'Custom CSS class',
        type: 'string',
        description: 'Optional class name(s) added to the block wrapper.',
      },
      preheaderText: {
        title: 'Preheader',
        type: 'string',
        description: 'Optional text that appears above the main header.',
      },
      headerText: {
        title: 'Header',
        type: 'string',
        widget: 'textarea',
        description: 'Main heading. Supports HTML tags like strong, em, and line breaks. Also used for TOC.',
      },
      descriptionText: {
        title: 'Description',
        type: 'string',
        widget: 'textarea',
        description: 'Description text that appears below the header.',
      },
      headerAlignment: {
        title: 'Header alignment',
        choices: [
          ['left', 'Left'],
          ['center', 'Center'],
          ['right', 'Right'],
        ],
        type: 'string',
        default: 'left',
      },
      blockAlignment: {
        title: 'Content alignment',
        choices: [
          ['left', 'Left'],
          ['center', 'Center'],
          ['right', 'Right'],
        ],
        type: 'string',
        default: 'left',
      },
      columns: {
        title: 'Number of columns (desktop)',
        type: 'number',
        minimum: 1,
        maximum: 6,
        default: 3,
      },
      imagePosition: {
        title: 'Image position',
        choices: [
          ['background', 'Background (default)'],
          ['above-card', 'Above card'],
          ['above-content', 'Above content (below title)'],
          ['below-content', 'Below content (above button)'],
          ['below-card', 'Below card'],
        ],
        type: 'string',
        default: 'background',
        description: 'How to display card images. Applies to all cards.',
      },
      blockBackgroundColor: {
        title: 'Block background color',
        choices: getColorChoices(blockBgColorList),
        type: 'string',
        default: 'none',
        description: 'Background color for the entire block section.',
      },
      cardBackgroundColor: {
        title: 'Card background color',
        widget: 'select',
        choices: getColorChoices(MASTER_COLOR_LIST),
        type: 'string',
        default: '',
        description: 'Background color for cards. Only shown when image is not background.',
      },
      showViewAll: {
        title: 'Show "View all" button',
        type: 'boolean',
        default: false,
        description: 'Show a link to the right of the block header.',
      },
      viewAllText: {
        title: '"View all" link text',
        type: 'string',
        description: 'Label for the view all link.',
      },
      viewAllUrl: {
        title: '"View all" link URL',
        widget: 'object_browser',
        mode: 'link',
        allowExternals: true,
        description: 'Destination for the view all link.',
      },
      viewAllStyle: {
        title: '"View all" button style',
        widget: 'select',
        choices: getButtonChoices(MASTER_COLOR_LIST),
        default: getDefaultButton(blockBg, MASTER_COLOR_LIST),
        description: 'Style for the view all link button.',
      },
      useStatistics: {
        title: 'Use statistics instead of cards',
        type: 'boolean',
        default: false,
        description: 'Cards are hidden (not deleted) and statistics are shown instead.',
      },
      statsFormatK: {
        title: 'Compact thousands into k for all stats',
        type: 'boolean',
        default: false,
      },
      statAnimationMs: {
        title: 'Animation duration (ms) for all stats',
        type: 'number',
        default: 6000,
        minimum: 200,
        maximum: 60000,
      },
      mobileCarousel: {
        title: 'Use carousel on mobile',
        type: 'boolean',
        default: false,
        description: 'On 768px and below, items become a swipeable carousel.',
      },
      mobileCarouselAutoplay: {
        title: 'Autoplay (mobile)',
        type: 'boolean',
        default: true,
      },
      mobileCarouselDots: {
        title: 'Show dots (mobile)',
        type: 'boolean',
        default: true,
      },
      carouselControlsColor: {
        title: 'Carousel controls color',
        choices: [
          ['light', 'Light (for dark backgrounds)'],
          ['dark', 'Dark (for light backgrounds)'],
        ],
        type: 'string',
        default: 'light',
      },
      items: {
        title: 'Cards',
        widget: showStats ? 'hidden' : 'object_list',
        default: [],
        schema: {
          title: 'Card',
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: ['preheader', 'title', 'description', 'linkText', 'url', 'anchor', 'buttonStyle', 'image'],
            },
          ],
          properties: {
            preheader: {
              title: 'Preheader',
              type: 'string',
              description: 'Optional text that appears above the title.',
            },
            title: { title: 'Title', type: 'string' },
            description: { title: 'Introduction', type: 'string' },
            linkText: { title: 'Link text', type: 'string' },
            url: {
              title: 'Link URL',
              widget: 'object_browser',
              mode: 'link',
              allowExternals: true,
            },
            anchor: {
              title: 'Anchor (optional)',
              type: 'string',
              description: 'Appends to link. If no link is set, this becomes the target.',
            },
            buttonStyle: {
              title: 'Button style',
              widget: 'select',
              choices: [
                ['arrow', 'Arrow icon'],
                ...getButtonChoices(MASTER_COLOR_LIST),
              ],
              type: 'string',
              default: getDefaultButton(cardBg, MASTER_COLOR_LIST),
              description: 'Arrow icon shows a circular arrow; link text is still used by screen readers.',
            },
            image: {
              title: 'Background image',
              widget: 'image',
            },
          },
          required: [],
        },
      },
      statistics: {
        title: 'Statistics',
        widget: showStats ? 'object_list' : 'hidden',
        default: [],
        schema: {
          title: 'Statistic',
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: ['label', 'value', 'suffix', 'extraInfo', 'url', 'anchor'],
            },
          ],
          properties: {
            label: { title: 'Label', type: 'string' },
            value: {
              title: 'Value (number)',
              type: 'number',
              description: 'Counts up from 0 to this value.',
            },
            suffix: { title: 'Suffix (e.g. % or +)', type: 'string' },
            extraInfo: { title: 'Extra info', type: 'string' },
            url: {
              title: 'Link URL (optional)',
              widget: 'object_browser',
              mode: 'link',
              allowExternals: true,
            },
            anchor: {
              title: 'Anchor (optional)',
              type: 'string',
              description: 'Appends to link. If no link is set, this becomes the target.',
            },
          },
          required: ['value'],
        },
      },
    },
    required: [],
  };
};

export default schema;
